import React, { useState, useEffect, useCallback } from "react";
import { X, CreditCard, DollarSign, Calendar, AlertCircle, Trash2, Receipt } from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { studentSubscriptionsAPI, financeAPI, notificationsAPI } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../App";
import { usePermissions } from "../HasPermission";
import { useTranslation } from "react-i18next";

const PaymentModal = ({ isOpen, onClose, subscription, onPaymentComplete }) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const canDeletePayments = hasPermission("delete:payments");

  const [formData, setFormData] = useState({
    paymentAmount: "",
    paymentMethod: "cash",
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Existing payments list
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Delete confirm dialog
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, payment: null, loading: false });

  // ── Load existing payments for this subscription ──────────────────────────
  const loadPayments = useCallback(async () => {
    if (!subscription?.organizationId || !subscription?.id) return;
    setPaymentsLoading(true);
    try {
      // Fetch all student payments in a wide date range and filter by subscriptionId
      const orgId = subscription.organizationId;
      const res = await financeAPI.getStudentPayments(orgId, "2020-01-01", "2099-12-31");
      const all = res?.data?.data || [];
      const filtered = all.filter(
        (p) =>
          p.subscriptionId === subscription.id ||
          (p.studentId === subscription.studentId &&
            (p.description || "").includes(subscription.id))
      );
      // Sort newest first
      filtered.sort((a, b) =>
        new Date(b.transactionDate || b.createdAt) - new Date(a.transactionDate || a.createdAt)
      );
      setPayments(filtered);
    } catch (err) {
      console.error("Failed to load payments:", err);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [subscription]);

  useEffect(() => {
    if (subscription && isOpen) {
      setFormData((prev) => ({
        ...prev,
        paymentAmount: (subscription.remainingAmount ?? 0).toString(),
      }));
      setErrors({});
      loadPayments();
    }
  }, [subscription, isOpen, loadPayments]);

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const amount = parseFloat(formData.paymentAmount);
    if (!formData.paymentAmount || amount <= 0)
      newErrors.paymentAmount = t("paymentModal.errors.amountGreaterThanZero");
    if (!formData.paymentDate)
      newErrors.paymentDate = t("paymentModal.errors.dateRequired");
    if (!formData.paymentMethod)
      newErrors.paymentMethod = t("paymentModal.errors.methodRequired");
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      await studentSubscriptionsAPI.addPayment(
        subscription.organizationId,
        subscription.id,
        {
          amount: parseFloat(formData.paymentAmount),
          paymentMethod: formData.paymentMethod,
          date: formData.paymentDate,
          notes: formData.notes,
        }
      );
      // Backend addPayment already creates a rich "Ödeme Alındı" notification
      // with full context (student, package, totals, actor). The previous extra
      // notification from this client was a duplicate, so it has been removed.
      onPaymentComplete();
      onClose();
    } catch (error) {
      console.error("Error processing payment:", error);
      setErrors({ submit: t("paymentModal.errors.submitFailed") });
    } finally {
      setLoading(false);
    }
  };

  // ── Delete payment ────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    const { payment } = deleteConfirm;
    if (!payment) return;
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));
    try {
      if (payment.subscriptionId && payment.subscriptionId !== "demo") {
        await studentSubscriptionsAPI.deletePayment(
          subscription.organizationId,
          payment.subscriptionId,
          payment.id
        );
      } else {
        await financeAPI.deletePayment(subscription.organizationId, payment.id);
      }
      setDeleteConfirm({ open: false, payment: null, loading: false });
      // Reload payments list and refresh subscription data
      await loadPayments();
      onPaymentComplete();
    } catch (err) {
      console.error("Failed to delete payment:", err);
      setDeleteConfirm((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        paymentAmount: "",
        paymentMethod: "cash",
        paymentDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen || !subscription) return null;

  const paymentMethods = [
    { value: "cash", label: t("paymentMethods.cash") },
    { value: "card", label: t("paymentMethods.card") },
    { value: "bank_transfer", label: t("paymentMethods.bankTransfer") },
    { value: "cheque", label: t("paymentMethods.cheque") },
    { value: "other", label: t("paymentMethods.other") },
  ];

  const remaining = subscription.remainingAmount ?? 0;
  const suggestedAmounts = [
    { label: t("paymentModal.fullAmount"), value: remaining },
    { label: t("paymentModal.halfAmount"), value: remaining / 2 },
    { label: "₺500", value: Math.min(500, remaining) },
    { label: "₺1000", value: Math.min(1000, remaining) },
  ].filter((a) => a.value > 0);

  const fmt = (n) =>
    new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);

  return (
    <>
      <div className="modal-overlay">
        <div
          className={`${colors.cardBg} rounded-2xl shadow-2xl border ${colors.cardBorder} w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden`}
        >
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CreditCard className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h3 className={`text-lg font-medium ${colors.textPrimary}`}>
                  {t("paymentModal.title")}
                </h3>
                <p className={`text-sm ${colors.textSecondary}`}>{subscription.packageName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className={`${colors.textMuted} hover:${colors.textSecondary} disabled:opacity-50 transition-colors`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Payment Summary */}
            <div className={`p-6 ${colors.bgTertiary} border-b ${colors.border}`}>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className={colors.textSecondary}>{t("paymentModal.totalPackagePrice")}</span>
                  <span className={`font-medium ${colors.textPrimary}`}>₺{fmt(subscription.totalPrice)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={colors.textSecondary}>{t("paymentModal.alreadyPaid")}</span>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    ₺{fmt(subscription.paidAmount)}
                  </span>
                </div>
                <div className={`flex justify-between text-lg font-semibold pt-2 border-t ${colors.border}`}>
                  <span className={colors.textPrimary}>{t("paymentModal.remainingBalance")}</span>
                  <span className={remaining > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}>
                    ₺{fmt(remaining)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <form id="payment-form" onSubmit={handleSubmit} className="p-6">
              {/* Quick Amount Selection */}
              {suggestedAmounts.length > 0 && (
                <div className="mb-4">
                  <label className="form-label">{t("paymentModal.quickSelectAmount")}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestedAmounts.map((amount, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, paymentAmount: amount.value.toString() }))
                        }
                        className={`px-3 py-2 text-sm border ${colors.inputBorder} ${colors.inputBg} rounded-lg ${colors.bgHover} focus:outline-none focus:ring-2 ${colors.inputFocus} transition-colors`}
                      >
                        <span className={colors.textPrimary}>{amount.label}</span>
                        <div className={`text-xs ${colors.textMuted}`}>₺{fmt(amount.value)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Amount */}
              <div className="mb-4">
                <label htmlFor="paymentAmount" className="form-label">
                  {t("paymentModal.paymentAmountLabel")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className={`w-4 h-4 ${colors.textMuted}`} />
                  </div>
                  <input
                    type="number"
                    id="paymentAmount"
                    name="paymentAmount"
                    min="0"
                    step="0.01"
                    value={formData.paymentAmount}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${errors.paymentAmount ? "border-red-300" : colors.inputBorder}`}
                    placeholder={t("paymentModal.paymentAmountPlaceholder")}
                    disabled={loading}
                  />
                </div>
                {errors.paymentAmount && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.paymentAmount}</p>
                )}
              </div>

              {/* Payment Method */}
              <div className="mb-4">
                <label htmlFor="paymentMethod" className="form-label">
                  {t("paymentModal.paymentMethodLabel")}
                </label>
                <select
                  id="paymentMethod"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${errors.paymentMethod ? "border-red-300" : colors.inputBorder}`}
                  disabled={loading}
                >
                  {paymentMethods.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                {errors.paymentMethod && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.paymentMethod}</p>
                )}
              </div>

              {/* Payment Date */}
              <div className="mb-4">
                <label htmlFor="paymentDate" className="form-label">
                  {t("paymentModal.paymentDateLabel")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className={`w-4 h-4 ${colors.textMuted}`} />
                  </div>
                  <input
                    type="date"
                    id="paymentDate"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${errors.paymentDate ? "border-red-300" : colors.inputBorder}`}
                    disabled={loading}
                  />
                </div>
                {errors.paymentDate && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.paymentDate}</p>
                )}
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label htmlFor="notes" className="form-label">{t("paymentModal.notesLabel")}</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={2}
                  value={formData.notes}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} rounded-lg focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent`}
                  placeholder={t("paymentModal.notesPlaceholder")}
                  disabled={loading}
                />
              </div>

              {/* Error */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-700 dark:text-red-300">{errors.submit}</p>
                  </div>
                </div>
              )}

              {/* Payment Preview */}
              {formData.paymentAmount && parseFloat(formData.paymentAmount) > 0 && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    {t("paymentModal.paymentPreview")}
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-300">{t("paymentModal.paymentAmountPreview")}</span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        ₺{fmt(parseFloat(formData.paymentAmount || 0))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-300">{t("paymentModal.newRemainingBalance")}</span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        ₺{fmt(Math.max(0, remaining - parseFloat(formData.paymentAmount || 0)))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-300">{t("paymentModal.newPaymentStatus")}</span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        {remaining - parseFloat(formData.paymentAmount || 0) <= 0
                          ? t("paymentModal.fullyPaid")
                          : t("paymentModal.partialPayment")}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </form>

            {/* ── Existing Payments Section ───────────────────────────────── */}
            <div className={`px-6 pb-6 border-t ${colors.border} pt-5`}>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className={`w-4 h-4 ${colors.textSecondary}`} />
                <span className={`text-[11px] font-black uppercase tracking-widest ${colors.textSecondary}`}>
                  Bu Pakete Ait Ödemeler
                </span>
                {payments.length > 0 && (
                  <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${colors.bgHoverStrong} ${colors.textSecondary}`}>
                    {payments.length} kayıt
                  </span>
                )}
              </div>

              {paymentsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner size="small" />
                </div>
              ) : payments.length === 0 ? (
                <p className={`text-[11px] ${colors.textTertiary} text-center py-3`}>
                  Bu paket için henüz ödeme kaydı yok.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {payments.map((pay) => (
                    <div
                      key={pay.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${colors.bgHoverStrong} border ${colors.border} group`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[13px] font-black text-emerald-500">
                            ₺{fmt(pay.amount)}
                          </span>
                          <span className={`text-[10px] font-medium ${colors.textTertiary}`}>
                            {pay.transactionDate
                              ? new Date(pay.transactionDate).toLocaleDateString("tr-TR")
                              : "—"}
                          </span>
                        </div>
                        {pay.recordedByName && (
                          <p className={`text-[9px] font-bold uppercase tracking-wider ${colors.textTertiary} mt-0.5`}>
                            {pay.recordedByName}
                          </p>
                        )}
                      </div>
                      {canDeletePayments && (
                        <button
                          onClick={() => setDeleteConfirm({ open: true, payment: pay, loading: false })}
                          className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 opacity-0 group-hover:opacity-100 transition-all"
                          title="Ödemeyi Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`flex space-x-3 p-6 border-t ${colors.border} shrink-0`}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={`flex-1 px-4 py-2 border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} rounded-lg ${colors.btnSecondaryHover} focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            >
              {t("paymentModal.cancel")}
            </button>
            <button
              type="submit"
              form="payment-form"
              disabled={loading || !formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="small" className="mr-2" />
                  {t("paymentModal.processing")}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t("paymentModal.addPayment")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() =>
              !deleteConfirm.loading &&
              setDeleteConfirm({ open: false, payment: null, loading: false })
            }
          />
          <div
            className={`relative ${colors.cardBg} rounded-2xl shadow-2xl border ${colors.cardBorder} w-full max-w-xs mx-4 p-6`}
          >
            <div className="flex items-center justify-center w-11 h-11 mx-auto mb-4 rounded-full bg-rose-100 dark:bg-rose-500/10">
              <Trash2 className="w-5 h-5 text-rose-500" />
            </div>
            <h3 className={`text-center text-[13px] font-black uppercase tracking-widest ${colors.textPrimary} mb-2`}>
              Ödemeyi Sil
            </h3>
            <p className={`text-center text-[11px] ${colors.textSecondary} mb-1`}>
              Bu ödemeyi silmek istediğinize emin misiniz?
            </p>
            {deleteConfirm.payment && (
              <div className={`mt-3 mb-5 ${colors.bgTertiary} rounded-xl p-3 border ${colors.border} space-y-1`}>
                <p className={`text-[10px] font-black uppercase ${colors.textTertiary}`}>Tutar</p>
                <p className="text-[15px] font-black text-rose-500">
                  ₺{fmt(deleteConfirm.payment.amount)}
                </p>
                <p className={`text-[9px] ${colors.textTertiary} mt-1`}>
                  ⚠️ Bu işlem paketin ödeme tutarını günceller.
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ open: false, payment: null, loading: false })}
                disabled={deleteConfirm.loading}
                className={`flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider ${colors.bgHoverStrong} ${colors.textSecondary} hover:opacity-80 transition-colors disabled:opacity-50`}
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteConfirm.loading}
                className="flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-lg shadow-rose-500/20 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {deleteConfirm.loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    Siliniyor...
                  </>
                ) : (
                  <><Trash2 className="w-3 h-3" /> Evet, Sil</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentModal;
